import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

interface BlockedReasonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  taskTitle: string;
}

export function BlockedReasonModal({ open, onOpenChange, onConfirm, taskTitle }: BlockedReasonModalProps) {
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    if (reason.trim()) {
      onConfirm(reason.trim());
      setReason('');
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive" />
            Mark as Blocked
          </DialogTitle>
          <DialogDescription>
            Why is "{taskTitle}" blocked? This helps you remember what's holding you back.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="blocked-reason">What's blocking this?</Label>
          <Textarea
            id="blocked-reason"
            placeholder="e.g., Waiting for response from..., Need to learn..., Depends on..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="resize-none"
            autoFocus
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!reason.trim()}>
            Mark Blocked
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DoneNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (note: string, evidence: string) => void;
  onSkip: () => void;
  taskTitle: string;
}

export function DoneNoteModal({ open, onOpenChange, onConfirm, onSkip, taskTitle }: DoneNoteModalProps) {
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState('');
  const [showSkipWarning, setShowSkipWarning] = useState(false);

  const handleConfirm = () => {
    onConfirm(note.trim(), evidence.trim());
    setNote('');
    setEvidence('');
    onOpenChange(false);
  };

  const handleSkip = () => {
    if (!showSkipWarning) {
      setShowSkipWarning(true);
      return;
    }
    onSkip();
    setNote('');
    setEvidence('');
    setShowSkipWarning(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-primary" />
            Task Complete!
          </DialogTitle>
          <DialogDescription>
            "{taskTitle}" is done. Capture what you learned or achieved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="done-note">What did you accomplish?</Label>
            <Textarea
              id="done-note"
              placeholder="e.g., Completed the prototype, Got 3 customer interviews..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="resize-none"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="evidence">Evidence or link (optional)</Label>
            <Textarea
              id="evidence"
              placeholder="e.g., Link to doc, screenshot, key insight..."
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>

          {showSkipWarning && (
            <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 rounded">
              Capturing what you did helps you remember and builds momentum. Are you sure?
            </p>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
            {showSkipWarning ? 'Yes, skip anyway' : 'Skip'}
          </Button>
          <Button onClick={handleConfirm}>
            Save & Complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}